// ============================ enums ============================

/** SOURCE: `Biod.Insights.Service.Constants.BepiCategory` */
export enum BepiCategory {
  Demographics = 6,
  Healthcare = 7,
  PublicHealth = 8,
  Economy = 9,
  DiseaseDynamics = 10,
  PoliticalInternational = 11,
  PoliticalDomestic = 12,
  BEPIOverall = 13,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.CaseLevelTrendType` */
export enum CaseLevelTrendType {
  Unknown = 0,
  Steady = 1,
  Rising = 2,
  Decreasing = 3,
  ConsistentlyDecreasing = 4,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.CaseLevelType` */
export enum CaseLevelType {
  Unknown = 0,
  Low = 1,
  Moderate = 2,
  High = 3,
}

/** SOURCE: `Biod.Insights.Service.Constants.ConnectivityGroupByType` */
export enum ConnectivityGroupByType {
  Airport = 1,
  County = 2,
  Province = 3,
  Country = 4,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.CoverageSufficiencyType` */
export enum CoverageSufficiencyType {
  Unknown = 0,
  Insufficient = 1,
  Sufficient = 2,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.CovidOutlookType` */
export enum CovidOutlookType {
  Unknown = 0,
  Poor = 1,
  Positive = 2,
  HasWarnings = 3,
}

/** SOURCE: `System.DayOfWeek` */
export enum DayOfWeek {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

/** SOURCE: `Biod.Insights.Service.Constants.DescriptionImportanceType` */
export enum DescriptionImportanceType {
  Standard = 1,
  Important = 2,
  Critical = 3,
}

/** SOURCE: `Biod.Insights.Service.Constants.DiseaseMovementSortType` */
export enum DiseaseMovementSortType {
  OriginLocationAToZ = 1,
  OriginLocationZToA = 2,
  DestinationLocationAToZ = 3,
  DestinationLocationZToA = 4,
  AffectedTravellerNumbersHighToLow = 5,
  AffectedTravellerNumbersLowToHigh = 6,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.ImmunityLevelType` */
export enum ImmunityLevelType {
  Unknown = 0,
  VeryLow = 1,
  Low = 2,
  Moderate = 3,
  High = 4,
  VeryHigh = 5,
}

/** SOURCE: `Biod.Insights.Service.Constants.LocationType` */
export enum LocationType {
  Unknown = 0,
  City = 2,
  County = 9,
  MetroArea = 30,
  Province = 4,
  Country = 6,
}

/** SOURCE: `Biod.Products.Common.Constants.OutbreakPotentialCategory` */
export enum OutbreakPotentialCategory {
  Sustained = 1,
  Sporadic = 2,
  NeedsMapSustained = 3,
  NeedsMapUnlikely = 4,
  Unlikely = 5,
  Unknown = 6,
}

/** SOURCE: `Biod.Insights.Service.Constants.PreventabilityDisruptionFactorType` */
export enum PreventabilityDisruptionFactorType {
  NoData = 0,
  WideProphylaxis = 1,
  WideVaccine = 2,
  LimitedVaccine = 3,
  NoVaccineOrProphylaxis = 4,
}

/** SOURCE: `Biod.Insights.Service.Constants.RiskFeedSortType` */
export enum RiskFeedSortType {
  MostAtRisk = 1,
  CaseIncidence = 2,
  ImportationRisk = 3,
  Severity = 4,
  Preventability = 5,
  Transmissibility = 6,
  LocationPreparedness = 7,
}

/** SOURCE: `Biod.Insights.Service.Constants.SeverityDisruptionFactorType` */
export enum SeverityDisruptionFactorType {
  NoData = 0,
  Mild = 1,
  MildToModerate = 2,
  Moderate = 3,
  ModerateToSevere = 4,
  Severe = 5,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.StringencyChangeType` */
export enum StringencyChangeType {
  Unknown = 0,
  RemainedStable = 1,
  Decreased = 2,
  Risen = 3,
}

/** SOURCE: `Biod.Insights.Service.Constants.TemporalAggregationCategory` */
export enum TemporalAggregationCategory {
  Day = 0,
  Week = 1,
  Month = 2,
  Year = 3,
  All = 4,
}

/** SOURCE: `Biod.Insights.Service.Constants.TransmissibilityDisruptionFactorType` */
export enum TransmissibilityDisruptionFactorType {
  NoData = 0,
  NotPossible = 1,
  NotPossibleUnlessLocally = 2,
  Unlikely = 3,
  LimitedViaHumanToHuman = 4,
  PossibleViaEnvironmentLocally = 5,
  PossibleViaEnvironment = 6,
  PossibleViaHumanToHuman = 7,
}

/** SOURCE: `Biod.Insights.Service.Constants.TravelVolumeSortType` */
export enum TravelVolumeSortType {
  OriginLocationAToZ = 1,
  OriginLocationZToA = 2,
  DestinationLocationAToZ = 3,
  DestinationLocationZToA = 4,
  AirVolumeHighToLow = 5,
  GroundVolumeHighToLow = 6,
  TotalVolumeHighToLow = 7,
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.UnderreportingStatementType` */
export enum UnderreportingStatementType {
  Unknown = 0,
  Likely = 1,
  None = 2,
}

/** SOURCE: `Biod.Insights.Service.Constants.VaccinationDataCategory` */
export enum VaccinationDataCategory {
  HighRiskPopulation = 0,
  TotalAdultPopulation = 1,
}

// ============================ classes ============================

/** SOURCE: `Biod.Insights.Service.Models.Disease.AcquisitionModeGroupModel` */
export interface AcquisitionModeGroupModel {
  acquisitionModes?: AcquisitionModeModel[];
  rankId?: number;
  rankName?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.AcquisitionModeModel` */
export interface AcquisitionModeModel {
  description?: string;
  id?: number;
  label?: string;
  modalityId?: number;
  modalityName?: string;
  rankId?: number;
  vectorId?: number;
  vectorName?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.ApplicationMetadataModel` */
export interface ApplicationMetadataModel {
  aggregatedExportationRiskThreshold?: number;
  aggregatedImportationRiskThreshold?: number;
  iataDatasetYear?: number;
  innovataDatasetYear?: number;
  landscanDatasetYear?: number;
  legacyEventIdMapping?: { [id: number]: CountryDiseaseActivityKey };
  maxUserLocations?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Article.ArticleModel` */
export interface ArticleModel {
  countryDiseaseActivityKey?: CountryDiseaseActivityKey;
  id?: string;
  originalLanguage?: string;
  publishedDate?: string;
  sourceName?: string;
  title?: string;
  url?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.BepiValue` */
export interface BepiValue {
  category?: BepiCategory;
  value?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Risk.CalculationBreakdownModel` */
export interface CalculationBreakdownModel {
  airports?: EventAirportModel;
  calculationCases?: EventCalculationCasesModel;
  diseaseInformation?: DiseaseInformationModel;
}

/** SOURCE: `Biod.Insights.Service.Models.CaseCountModel` */
export interface CaseCountModel {
  confirmedCases?: number;
  deaths?: number;
  hasConfirmedCasesNesting?: boolean;
  hasDeathsNesting?: boolean;
  hasReportedCasesNesting?: boolean;
  hasSuspectedCasesNesting?: boolean;
  locationId?: number;
  reportedCases?: number;
  suspectedCases?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.CaseCount.CaseIncidenceHierarchyModel` */
export interface CaseIncidenceHierarchyModel {
  casesPerPopulation?: number;
  hasReportedData?: boolean;
  isInherited?: boolean;
  parentLocationName?: string;
  parentLocationReportedCases?: number;
  population?: number;
  reportedCases?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Nesting.CaseReportModel` */
export interface CaseReportModel {
  articles?: ArticleModel[];
  caseCounts?: CaseCountModel;
  countryId?: number;
  countyId?: number;
  locationId?: number;
  locationName?: string;
  locationType?: number;
  metroAreaId?: number;
  provinceId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.CountryDescriptionListModel` */
export interface CountryDescriptionListModel {
  countryName?: string;
  descriptionList?: DescriptionReportModel[];
  lastUpdateDate?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.CountryDiseaseActivityKey` */
export interface CountryDiseaseActivityKey {
  countryId?: number;
  diseaseActivityId?: number;
  diseaseId?: number;
  speciesId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.CountryTemporalActivityModel` */
export interface CountryTemporalActivityModel {
  countryDiseaseActivityKey?: CountryDiseaseActivityKey;
  temporalActivityList?: TemporalActivityModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.CustomDiseaseGroup.CreateCustomDiseaseGroupModel` */
export interface CreateCustomDiseaseGroupModel {
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.CustomDiseaseGroup.CustomDiseaseGroup` */
export interface CustomDiseaseGroup {
  diseaseIds?: number[];
  id?: number;
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.DailyActivityListModel` */
export interface DailyActivityListModel {
  dailyReports?: DailyActivityModel[];
  hasNewerEntries?: boolean;
  hasOlderEntries?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.DailyActivityModel` */
export interface DailyActivityModel {
  articleCount?: number;
  caseCounts?: HierarchicalCaseCountModel;
  countryDescriptions?: CountryDescriptionListModel[];
  reportDate?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.DescriptionReportModel` */
export interface DescriptionReportModel {
  articles?: ArticleModel[];
  articleSourceNames?: string[];
  countryId?: number;
  countyId?: number;
  description?: string;
  hasBeenTruncated?: boolean;
  hasHtmlTagsInDescription?: boolean;
  importance?: DescriptionImportanceType;
  locationId?: number;
  locationName?: string;
  metroAreaId?: number;
  provinceId?: number;
  reportDate?: string;
  title?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.DiseaseActivityDescriptionsModel` */
export interface DiseaseActivityDescriptionsModel {
  descriptions?: DescriptionReportModel[];
  total?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.DiseaseActivityDescriptionsRequestModel` */
export interface DiseaseActivityDescriptionsRequestModel {
  count?: number;
  diseaseId?: number;
  endDate?: string;
  locationId?: number;
  speciesId?: number;
  startDate?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseGroupModel` */
export interface DiseaseGroupModel {
  diseaseIds?: number[];
  groupId?: number;
  groupName?: string;
  subGroups?: DiseaseGroupModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseInformationModel` */
export interface DiseaseInformationModel {
  acquisitionModeGroups?: AcquisitionModeGroupModel[];
  agents?: string;
  agentTypes?: string;
  biosecurityRisk?: string;
  diseaseOverview?: DiseaseOverviewModel[];
  firstActivityUpdateDate?: string;
  id?: number;
  incubationPeriod?: string;
  lastActivityUpdateDate?: string;
  name?: string;
  outbreakPotentialAttributeId?: number;
  preventionMeasure?: string;
  symptomaticPeriod?: string;
  transmissionModes?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseLocationMatrixEntryModel` */
export interface DiseaseLocationMatrixEntryModel {
  casesPerPopulation?: number;
  disruption?: DisruptionModel;
  hasReportedData?: boolean;
  importationRisk?: RiskModel;
  isInherited?: boolean;
  locationId?: number;
  parentLocationName?: string;
  parentLocationReportedCases?: number;
  reportedCases?: number;
  riskPriorityScore?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseLocationMatrixModel` */
export interface DiseaseLocationMatrixModel {
  caseCounts?: CaseCountModel;
  exportationRisk?: RiskModel;
  locationEntries?: DiseaseLocationMatrixEntryModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseMortalityRatesModel` */
export interface DiseaseMortalityRatesModel {
  treatedMax?: number;
  treatedMin?: number;
  untreatedMax?: number;
  untreatedMin?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.DiseaseRiskVolumeModel` */
export interface DiseaseRiskVolumeModel {
  diseaseId?: number;
  diseaseName?: string;
  volume?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.DiseaseMovementModel` */
export interface DiseaseMovementModel {
  sourceTravelLocation?: TravelLocationModel;
  destinationTravelLocation?: TravelLocationModel;
  diseaseRiskVolumes?: DiseaseRiskVolumeModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.DiseaseMovementListModel` */
export interface DiseaseMovementListModel {
  diseaseMovements?: DiseaseMovementModel[];
  total?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseMovement.DiseaseMovementRequestModel` */
export interface DiseaseMovementRequestModel {
  sourceLocationId?: number;
  sourceGroupBy?: ConnectivityGroupByType;
  destinationLocationId?: number;
  destinationGroupBy?: ConnectivityGroupByType;
  dateFrom: Date;
  pageSize?: number;
  sortBy?: DiseaseMovementSortType;
  skipRows?: number;
  includeDomestic: boolean;
  diseaseIds: number[];
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseOverviewModel` */
export interface DiseaseOverviewModel {
  body?: string;
  header?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseRelevanceSettingsModel` */
export interface DiseaseRelevanceSettingsModel {
  alwaysNotifyDiseaseIds?: number[];
  neverNotifyDiseaseIds?: number[];
  riskOnlyDiseaseIds?: number[];
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseRiskModel` */
export interface DiseaseRiskModel {
  caseCounts?: CaseCountModel;
  descriptionTypes?: DescriptionImportanceType[];
  diseaseInformation?: DiseaseInformationModel;
  exportationRisk?: RiskModel;
  hasCaseDataActivity?: boolean;
  hasLocalEvents?: boolean;
  importationRisk?: RiskModel;
  lastUpdatedEventDate?: string;
  outbreakPotentialCategory?: OutbreakPotentialCategoryModel;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.DiseaseTemporalActivityModel` */
export interface DiseaseTemporalActivityModel {
  diseaseActivityId?: number;
  diseaseId?: number;
  speciesId?: number;
  temporalActivityList?: TemporalActivityModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.DisruptionModel` */
export interface DisruptionModel {
  locationResiliency?: number;
  preventability?: PreventabilityDisruptionFactorType;
  severity?: SeverityDisruptionFactorType;
  transmissibility?: TransmissibilityDisruptionFactorType;
}

/** SOURCE: `Biod.Insights.Service.Models.EmailType.EmailTypeModel` */
export interface EmailTypeModel {
  id?: number;
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.EventAirportModel` */
export interface EventAirportModel {
  destinationAirports?: GetAirportRiskModel[];
  sourceAirports?: GetAirportRiskModel[];
  totalDestinationAirports?: number;
  totalDestinationVolume?: number;
  totalSourceAirports?: number;
  totalSourceVolume?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.EventCalculationCasesModel` */
export interface EventCalculationCasesModel {
  casesIncluded?: number;
  maxCasesIncluded?: number;
  minCasesIncluded?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.EventInformationModel` */
export interface EventInformationModel {
  countryName?: string;
  diseaseActivityKey?: CountryDiseaseActivityKey;
  diseaseId?: number;
  endDate?: string;
  firstActivityUpdateDate?: string;
  id?: number;
  lastActivityUpdateDate?: string;
  lastUpdatedDate?: string;
  startDate?: string;
  summary?: string;
  title?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.EventLocationModel` */
export interface EventLocationModel {
  caseCounts?: CaseCountModel;
  countryDiseaseActivityKey?: CountryDiseaseActivityKey;
  countryName?: string;
  countyId?: number;
  countyName?: string;
  eventDate?: string;
  geonameId?: number;
  locationName?: string;
  locationType?: number;
  metroAreaId?: number;
  metroAreaName?: string;
  previousEventDate?: string;
  provinceId?: number;
  provinceName?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Map.EventsPinModel` */
export interface EventsPinModel {
  events?: EventInformationModel[];
  geonameId?: number;
  locationName?: string;
  point?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Airport.GetAirportModel` */
export interface GetAirportModel {
  id: number;
  code?: string;
  name?: string;
  displayName?: string;
  city?: string;
  province?: string;
  country?: string;
  latitude?: number;
  longitude?: number;
  volume?: number;
  countryId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Airport.GetAirportRiskModel` */
export interface GetAirportRiskModel {
  cases?: EventCalculationCasesModel;
  city?: string;
  code?: string;
  exportationRisk?: RiskModel;
  id?: number;
  importationRisk?: RiskModel;
  latitude?: number;
  longitude?: number;
  maxPrevalence?: number;
  minPrevalence?: number;
  name?: string;
  population?: number;
  volume?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.GetEventListModel` */
export interface GetEventListModel {
  countryPins?: EventsPinModel[];
  diseaseInformation?: DiseaseInformationModel;
  eventsList?: GetEventModel[];
  exportationRisk?: RiskModel;
  importationRisk?: RiskModel;
  outbreakPotentialCategory?: OutbreakPotentialCategoryModel;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.GetEventModel` */
export interface GetEventModel {
  airports?: EventAirportModel;
  articles?: ArticleModel[];
  articleSourceNames?: string[];
  calculationMetadata?: EventCalculationCasesModel;
  caseCounts?: CaseCountModel;
  descriptionTypes?: DescriptionImportanceType[];
  diseaseInformation?: DiseaseInformationModel;
  eventInformation?: EventInformationModel;
  eventLocations?: EventLocationModel[];
  exportationRisk?: RiskModel;
  importationRisk?: RiskModel;
  isLocal?: boolean;
  outbreakPotentialCategory?: OutbreakPotentialCategoryModel;
  previousActivityDate?: string;
  updatedLocations?: EventLocationModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.Geoname.GetGeonameModel` */
export interface GetGeonameModel {
  country?: string;
  countryId?: number;
  fullDisplayName?: string;
  geonameId?: number;
  latitude?: number;
  locationType?: number;
  longitude?: number;
  name?: string;
  province?: string;
  shape?: string;
  hasProvinces?: boolean;
  hasCounties?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.Geoname.GetGeonameShapesModel` */
export interface GetGeonameShapesModel {
  geonameIds?: number[];
  includeShape?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.User.GetUserLocationModel` */
export interface GetUserLocationModel {
  geonames?: GetGeonameModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.Event.HierarchicalCaseCountModel` */
export interface HierarchicalCaseCountModel {
  articles?: ArticleModel[];
  countryCaseCounts?: CaseReportModel;
  countyCaseCounts?: CaseReportModel;
  globalCaseCounts?: CaseCountModel;
  locationCaseCounts?: CaseReportModel;
  metroAreaCaseCounts?: CaseReportModel;
  provinceCaseCounts?: CaseReportModel;
}

/** SOURCE: `Biod.Insights.Service.Models.Risk.ImportationSourcesRequestModel` */
export interface ImportationSourcesRequestModel {
  diseaseActivityId?: number;
  locationId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Logging.JsLogModel` */
export interface JsLogModel {
  logLevel?: string;
  message?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.LocationBepiModel` */
export interface LocationBepiModel {
  bepiValues?: BepiValue[];
  locationId?: number;
  locationName?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.LocationRiskSummaryRequestModel` */
export interface LocationRiskSummaryRequestModel {
  activityEndDate?: string;
  activityStartDate?: string;
  diseaseIds?: number[];
  locationIds?: number[];
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.CurrentCasesModel` */
export interface LocationVaccinationCasesModel {
  casesOutlook?: CovidOutlookType;
  caseLevel?: CaseLevelType;
  caseLevelTrend?: CaseLevelTrendType;
  underreporting?: UnderreportingStatementType;
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.GovernmentStringencyModel` */
export interface LocationVaccinationGovernmentStringencyModel {
  stringencyChange?: StringencyChangeType;
}

/** SOURCE: `BlueDot.Libraries.Core.VaccineHeuristics.ImmunityModel` */
export interface LocationVaccinationImmunityModel {
  immunityOutlook?: CovidOutlookType;
  immunityLevel?: ImmunityLevelType;
  coverageSufficiency?: CoverageSufficiencyType;
}

/** SOURCE: `Biod.Insights.Service.Models.Vaccine.LocationVaccinationRateModel` */
export interface LocationVaccinationRateModel {
  dailyNewCasesPer100000?: number;
  dailyVaccinationRate?: number;
  dailyFullVaccinationRate?: number;
  historicalVaccinationData?: VaccinationDataEntryModel[];
  immuneUnvaccinatedRate?: number;
  isReleasable: boolean;
  locationId?: number;
  predictedVaccinationData?: VaccinationDataEntryModel[];
  targetVaccinationRateDate?: Date; // TODO: these should be a string, since it comes from API, please create a mapping type fopr Date revival
  targetVaccinationRateWorstDate?: Date;
  targetVaccinationRateBestDate?: Date;
  immuneUnvaccinatedLastUpdatedDate?: Date;
  testPositivityLast14Days?: number;
  totalVaccinationRate?: number;
  totalFullVaccinationRate?: number;
  totalBoostersPer100?: number;
  stringencyIndex?: number;
  stringencyIndexChange?: number;
  hasStringencyFallbackOccurred?: boolean;
  hasTestPositivityFallbackOccurred?: boolean;
  summary: LocationVaccinationSummaryModel;
}

/** SOURCE: `Biod.Insights.Service.Models.Vaccine.SummaryModel` */
export interface LocationVaccinationSummaryModel {
  currentCases?: LocationVaccinationCasesModel;
  immunity?: LocationVaccinationImmunityModel;
  governmentStringency?: LocationVaccinationGovernmentStringencyModel;
}

/** SOURCE: `BlueDot.Libraries.Common.Utils.LongRange` */
export interface LongRange {
  min?: number;
  average?: number;
  max?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.User.ModifyUserModel` */
export interface ModifyUserModel {
  email?: string;
  firstName?: string;
  groupId?: number;
  id?: string;
  lastName?: string;
  locationGeonameId?: number;
  organization?: string;
  phoneNumber?: string;
  userTypeId?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.UserType.ModifyUserTypeModel` */
export interface ModifyUserTypeModel {
  name?: string;
  notificationDescription?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.OutbreakPotentialCategoryModel` */
export interface OutbreakPotentialCategoryModel {
  attributeId?: number;
  diseaseId?: number;
  id?: number;
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.PostUserLocationModel` */
export interface PostUserLocationModel {
  geonameId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.ProximalCaseCountModel` */
export interface ProximalCaseCountModel {
  countryId?: number;
  diseaseId?: number;
  isWithinLocation?: boolean;
  locationId?: number;
  locationName?: string;
  locationType?: number;
  proximalCases?: CaseCountModel;
  speciesId?: number;
  totalEventCases?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.RiskAggregationModel` */
export interface RiskAggregationModel {
  countryPins?: EventsPinModel[];
  diseaseRisks?: DiseaseRiskModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.RiskFeedEntryModel` */
export interface RiskFeedEntryModel {
  acquisitionSources?: string[];
  caseIncidence?: CaseIncidenceHierarchyModel;
  diseaseActivityId?: number;
  diseaseId?: number;
  disruption?: DisruptionModel;
  distributedCasesLocationName?: string;
  hasReportedData?: boolean;
  importationRisk?: RiskModel;
  isTreatmentAvailable?: boolean;
  locationId?: number;
  mortalityRates?: DiseaseMortalityRatesModel;
  reportedCases?: number;
  riskFactorNumber?: number;
  riskPriorityScore?: number;
  speciesId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.RiskFeedIncidenceModel` */
export interface RiskFeedIncidenceModel {
  caseCounts?: HierarchicalCaseCountModel;
  hasReportedData?: boolean;
  lastUpdatedDate?: string;
  temporalActivityList?: TemporalActivityModel[];
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.RiskFeedPosition` */
export interface RiskFeedPosition {
  diseaseActivityId?: number;
  locationId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.RiskFeed.RiskFeedRequestModel` */
export interface RiskFeedRequestModel {
  activityEndDate?: string;
  activityStartDate?: string;
  diseaseIds?: number[];
  lastEntry?: RiskFeedPosition;
  locationIds?: number[];
  pageSize?: number;
  sortBy?: RiskFeedSortType;
}

/** SOURCE: `Biod.Insights.Service.Models.RiskModel` */
export interface RiskModel {
  isModelNotRun?: boolean;
  isModelRunning?: boolean;
  maxMagnitude?: number;
  maxProbability?: number;
  minMagnitude?: number;
  minProbability?: number;
  userLocationId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.DiseaseActivity.TemporalActivityModel` */
export interface TemporalActivityModel {
  caseCounts?: CaseCountModel;
  countryCaseCounts?: CaseCountModel;
  descriptions?: DescriptionReportModel[];
  endDate?: string;
  startDate?: string;
  totalDescriptionCount?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.TemporalActivityRequestModel` */
export interface TemporalActivityRequestModel {
  activityEndDate?: string;
  activityStartDate?: string;
  aggregationCategory?: TemporalAggregationCategory;
  aggregationTime?: number;
  countryId?: number;
  descriptionCountForAggregationTime?: number;
  diseaseId?: number;
  includeProximalActivity?: boolean;
  locationId?: number;
  speciesId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.Event.TemporalCaseCountModel` */
export interface TemporalCaseCountModel {
  caseCounts?: CaseCountModel;
  endDate?: string;
  startDate?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.Disease.TemporalCaseCountRequestModel` */
export interface TemporalCaseCountRequestModel {
  aggregationCategory?: TemporalAggregationCategory;
  aggregationTime?: number;
  caseCountEndDate?: string;
  caseCountStartDate?: string;
  diseaseId?: number;
  eventId?: number;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.TravelVolumeRequestModel` */
export interface TravelVolumeRequestModel {
  sourceLocationId?: number;
  sourceGroupBy?: ConnectivityGroupByType;
  destinationLocationId?: number;
  destinationGroupBy?: ConnectivityGroupByType;
  pageSize?: number;
  sortBy?: TravelVolumeSortType;
  skipRows?: number;
  includeDomestic?: boolean;
  includeAirTravel?: boolean;
  includeGroundTravel?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.TravelLocationModel` */
export interface TravelLocationModel {
  locationId?: number;
  locationType?: LocationType;
  locationName?: string;
  locationCode?: string;
  county?: string;
  city?: string;
  province?: string;
  country?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.TravelVolumeModel` */
export interface TravelVolumeModel {
  sourceTravelLocation?: TravelLocationModel;
  destinationTravelLocation?: TravelLocationModel;
  airTravelVolume?: number;
  groundTravelVolume?: LongRange;
  isGroundTravelSupported?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.TravelVolume.TravelVolumeListModel` */
export interface TravelVolumeListModel {
  travelVolumes?: TravelVolumeModel[];
  total?: number;
  aggregatedAirVolume?: number;
  aggregatedGroundVolume?: LongRange;
  date?: Date;
}

/** SOURCE: `Biod.Insights.Service.Models.CustomDiseaseGroup.UpdateCustomDiseaseGroupModel` */
export interface UpdateCustomDiseaseGroupModel {
  diseaseIds?: number[];
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserCustomSettingsModel` */
export interface UserCustomSettingsModel {
  diseaseRelevanceSettings?: DiseaseRelevanceSettingsModel;
  geonameIds?: number[];
  isPresetSelected?: boolean;
  userTypeId?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserGroupModel` */
export interface UserGroupModel {
  id?: number;
  name?: string;
  userIds?: string[];
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserInitRequestModel` */
export interface UserInitRequestModel {
  userId?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserModel` */
export interface UserModel {
  aoiGeonameIds?: string;
  creationDate?: string;
  diseaseRelevanceSetting?: DiseaseRelevanceSettingsModel;
  firstLoginDate?: string;
  groupId?: number;
  id?: string;
  isActive?: boolean;
  isDoNotTrack?: boolean;
  isEmailConfirmed?: boolean;
  isExistingAccount?: boolean;
  isExternal?: boolean;
  location?: GetGeonameModel;
  notificationsSetting?: UserNotificationsModel;
  personalDetails?: UserPersonalDetailsModel;
  roles?: UserRoleModel[];
  userName?: string;
  userType?: UserTypeModel;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserNotificationsModel` */
export interface UserNotificationsModel {
  dailyEmailDeliverySettings?: UserRecurringNotificationModel;
  email?: string;
  isDailyBriefEmailEnabled?: boolean;
  isEventEmailEnabled?: boolean;
  isNewActivityEmailEnabled?: boolean;
  isProximalEmailEnabled?: boolean;
  isWeeklyBriefEmailEnabled?: boolean;
  isWeeklyEmailEnabled?: boolean;
  weeklyEmailDeliverySettings?: UserRecurringNotificationModel;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserPersonalDetailsModel` */
export interface UserPersonalDetailsModel {
  email?: string;
  firstName?: string;
  lastName?: string;
  locationGeonameId?: number;
  organization?: string;
  phoneNumber?: string;
  userTypeId?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserRecurringNotificationModel` */
export interface UserRecurringNotificationModel {
  deliveryDay?: DayOfWeek;
  deliveryTime?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.User.UserRoleModel` */
export interface UserRoleModel {
  id?: string;
  isPublic?: boolean;
  name?: string;
}

/** SOURCE: `Biod.Insights.Service.Models.UserType.UserTypeModel` */
export interface UserTypeModel {
  assignedUsers?: UserModel[];
  id?: string;
  name?: string;
  notificationDescription?: string;
  relevanceSettings?: DiseaseRelevanceSettingsModel;
}

/** SOURCE: `Biod.Insights.Service.Models.Vaccine.VaccinationDataEntryModel` */
export interface VaccinationDataEntryModel {
  dailyNewCasesPer100000?: number;
  dailyNewCasesTrend?: number;
  dailyVaccinationRate?: number;
  date?: Date; // TODO: this should be a string, since it comes from API, please create a mapping type fopr Date revival
  testPositivityLast14Days?: number;
  vaccinationRateBest?: number;
  vaccinationRate?: number;
  vaccinationRateWorst?: number;
  stringencyIndex?: number;
  stringencyIndexChange?: number;
  totalBoostersPer100?: number;
  hasStringencyFallbackOccurred?: boolean;
}

/** SOURCE: `Biod.Insights.Service.Models.DataSuite.VaccinationDataRequestModel` */
export interface VaccinationDataRequestModel {
  category?: VaccinationDataCategory;
  endDate?: string;
  locationIds?: number[];
  startDate?: string;
  targetRate?: number;
}
